import store from "@/store";
import {
  getModule,
  Module,
  VuexModule,
  MutationAction,
  Mutation,
} from "vuex-module-decorators";
import { doGet } from "@/store/common/module/apis";
import { Client, Entity } from "@/store/common/model/corporate";
import {
  Garage,
  TariffName,
  TariffCategory,
  Zone,
  Account,
  Pump,
  TaxSlab,
  VTCat,
  Consignor,
  NameMaster,
} from "@/store/common/model/master";
import auth from "@/store/user/module/auth";

@Module({
  namespaced: true,
  name: "corpService",
  store,
  dynamic: true,
})
class CorpServiceModule extends VuexModule {
  clients: Client[] = [];
  vendors: Client[] = [];
  bus: { [key: string]: Entity[] } = {};
  taxes: { [key: string]: TaxSlab[] } = {};
  templates: { [key: string]: VTCat[] } = {};

  consignorsPOSO: { [key: string]: Consignor[] } = {};
  consignors: { [key: string]: Consignor[] } = {};

  nameMastersPOSO: { [key: string]: NameMaster[] } = {};
  nameMasters: { [key: string]: NameMaster[] } = {};
  garages: Garage[] = [];
  tariffNames: TariffName[] = [];
  tariffCategories: TariffCategory[] = [];
  zones: Zone[] = [];
  accounts: Account[] = [];
  pumps: Pump[] = [];
  vehicleTypes: VTCat[] = [];
  vehicleCategories: VTCat[] = [];
  // cities: { [key: string]: City[] } = {};

  @MutationAction
  async populateClients() {
    try {
      let clients = this.state ? this.state["clients"] : null;

      if (clients.length == 0) {
        clients = await doGet("/api/corp/clients/list?withSelf=true");
        if (clients && clients.length > 0) {
          this.clients = clients as Client[];
          return { clients: clients };
        }
      }
      return { clients: clients };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @Mutation
  clearClients() {
    this.clients = [];
    return { clients: [] };
  }

  @MutationAction
  async populateVendors() {
    try {
      if (this.vendors == undefined) {
        const vendors = await doGet("/api/vendors/map/list?withSelf=true");
        if (vendors && vendors.length > 0) {
          this.vendors = vendors as Client[];
        }
        return { vendors: this.vendors };
      }
      return { vendors: this.vendors };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @Mutation
  clearVendors() {
    this.vendors = [];
    return { vendors: [] };
  }

  @MutationAction
  async populateBUs(corp: string) {
    const entities = this.state ? this.state["bus"] : null;
    try {
      if (corp == null || corp == "") return { bus: entities };
      if (entities[corp] == undefined) {
        const cs = await doGet("/api/entity/" + corp + "/entity/list");
        if (cs && cs.length > 0) {
          entities[corp] = cs;
        }
        return { bus: entities };
      }
    } catch (error) {
      return Promise.reject(error);
    }
    return { bus: entities };
  }

  @MutationAction
  async populateGarages() {
    try {
      let garages = this.state ? this.state["garages"] : null;
      await auth.doPostLogin();
      if (garages.length == 0 && auth.authContent) {
        garages = await doGet(
          "/api/corp/garage/list/" + auth.authContent.adminOf + "/all"
        );
        if (garages && garages.length > 0) {
          this.garages = garages as Garage[];
          return { garages: garages };
        }
      }
      return { garages: garages };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populatePumps() {
    try {
      let pumps = this.state ? this.state["pumps"] : null;
      if (pumps.length == 0) {
        pumps = await doGet("/api/fuelpump/getAllFuelPump");
        if (pumps && pumps.length > 0) {
          this.pumps = pumps as Pump[];
          return { pumps: pumps };
        }
      }
      return { pumps: pumps };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateTariffNames() {
    try {
      let tariffNames = this.state ? this.state["tariffNames"] : null;
      if (tariffNames.length == 0) {
        tariffNames = await doGet("/api/tariff/names/all");
        if (tariffNames && tariffNames.length > 0) {
          this.tariffNames = tariffNames as TariffName[];
          return { tariffNames: tariffNames };
        }
      }
      return { tariffNames: tariffNames };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @Mutation
  clearTariffNames() {
    this.tariffNames = [];
    return { tariffNames: [] };
  }

  @MutationAction
  async populateTariffCategories() {
    try {
      let tariffCategories = this.state ? this.state["tariffCategories"] : null;
      if (tariffCategories.length == 0) {
        tariffCategories = await doGet("/api/tariffCategories/list");
        if (tariffCategories && tariffCategories.length > 0) {
          this.tariffCategories = tariffCategories as TariffName[];
          return { tariffCategories: tariffCategories };
        }
      }
      return { tariffCategories: tariffCategories };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @Mutation
  clearTariffCategories() {
    this.tariffCategories = [];
    return { tariffCategories: [] };
  }

  @MutationAction
  async populateZones() {
    try {
      let zones = this.state ? this.state["zones"] : null;
      if (zones.length == 0) {
        zones = await doGet("/api/corp/zones/get");
        if (zones && zones.length > 0) {
          this.zones = zones as Zone[];
          return { zones: zones };
        }
      }
      return { zones: zones };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @Mutation
  clearZones() {
    this.zones = [];
    return { zones: [] };
  }

  @MutationAction
  async populateAccounts() {
    try {
      let accounts = this.state ? this.state["accounts"] : null;
      if (accounts.length == 0) {
        accounts = await doGet("/api/expenseaccount/list");
        if (accounts && accounts.length > 0) {
          this.accounts = accounts as Account[];
          return { accounts: accounts };
        }
      }
      return { accounts: accounts };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateTaxes(corp: string) {
    const taxes = this.state ? this.state["taxes"] : null;
    try {
      let localCorp = "";
      if (corp) {
        localCorp = corp;
      } else {
        localCorp = "self";
      }
      if (!taxes[localCorp]) {
        let url = "/api/taxslab/all";
        if (corp) {
          url = url + "?client_id=" + corp;
        }

        const cs = await doGet(url);
        if (cs && cs.length > 0) {
          taxes[localCorp] = cs;
        }
        return { taxes: taxes };
      }
    } catch (error) {
      return Promise.reject(error);
    }
    return { taxes: taxes };
  }

  @MutationAction
  async populateTemplates(_key: string) {
    const templates = this.state ? this.state["templates"] : null;
    try {
      if (_key) {
        const keys = _key.split("_");
        if (!templates[_key]) {
          const tem = await doGet(
            "/api/template/" + keys[0] + "/options?forUpload=" + keys[1]
          );
          if (tem && tem.length > 0) {
            templates[_key] = tem;
          }
          return { templates: templates };
        }
      }
    } catch (error) {
      return Promise.reject(error);
    }
    return { templates: templates };
  }

  @MutationAction
  async populateVehicleTypes() {
    try {
      let vehicleTypes = this.state ? this.state["vehicleTypes"] : null;
      if (vehicleTypes.length == 0) {
        vehicleTypes = await doGet("/api/vehicletype/list");
        if (vehicleTypes && vehicleTypes.length > 0) {
          this.vehicleTypes = vehicleTypes as VTCat[];
          return { vehicleTypes: vehicleTypes };
        }
      }
      return { vehicleTypes: vehicleTypes };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateVehicleCategories() {
    try {
      let vehicleCategories = this.state
        ? this.state["vehicleCategories"]
        : null;
      if (vehicleCategories.length == 0) {
        vehicleCategories = await doGet("/api/vtgroup/vehicle/category/map");
        if (vehicleCategories && vehicleCategories.length > 0) {
          this.vehicleCategories = vehicleCategories as VTCat[];
          return { vehicleCategories: vehicleCategories };
        }
      }
      return { vehicleCategories: vehicleCategories };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @Mutation
  clearConsignors() {
    this.consignors = this.consignorsPOSO;
    return { consignors: [] };
  }

  @MutationAction
  async populateConsignors(type: string) {
    try {
      const consignors = this.state ? this.state["consignors"] : null;
      if (consignors.length == 0 || !consignors[type]) {
        const cs = await doGet("/api/consignor/" + type + "/all");
        if (cs && cs.length > 0) {
          consignors[type] = cs as Consignor[];
          return { consignors: consignors };
        }
      }
      return { consignors: consignors };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async repopulateNameMaster(type: string) {
    try {
      let nameMasters = this.state ? this.state["nameMasters"] : null;
      nameMasters = !nameMasters ? this.nameMastersPOSO : nameMasters;
      const cs = await doGet("/api/name/master/" + type + "/all");
      if (cs && cs.length > 0) {
        nameMasters[type] = cs as NameMaster[];
        return { nameMasters: nameMasters };
      }
      return { nameMasters: nameMasters };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @MutationAction
  async populateNameMasters(type: string) {
    try {
      const nameMasters = this.state ? this.state["nameMasters"] : null;
      if (nameMasters.length == 0 || !nameMasters[type]) {
        const cs = await doGet("/api/name/master/" + type + "/all");
        if (cs && cs.length > 0) {
          nameMasters[type] = cs as NameMaster[];
          return { nameMasters: nameMasters };
        }
      }
      return { nameMasters: nameMasters };
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

export default getModule(CorpServiceModule);
